// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The four modals.
 *
 * Everything factual in here was verified while the tool was built. Two things
 * in particular are stated rather than glossed: that free client-side depth-map
 * tools already exist, and that a browser cannot invent what was behind the
 * subject. A page that oversold either would be more comfortable to write and
 * would be worth less.
 */

import { term } from './glossary.ts';
import { MODEL_ID, RUNTIME_BYTES_OVER_THE_WIRE, TIERS } from './model/config.ts';

const mb = (bytes: number) => `${Math.round(bytes / 1e6)} MB`;

export const HOW_IT_WORKS = /* html */ `
  <p class="lede">
    A photograph is a flat sheet of colour. To move a camera through it you need to know how far away
    everything is — and that was never recorded. So unflatten works it out, and then builds the scene
    it implies.
  </p>

  <ol class="steps">
    <li>
      <h3>Your photograph is read, and stays here</h3>
      <p>
        It is decoded in this tab, from your own disk. Nothing is sent anywhere. If you are offline it
        works exactly the same, provided you have opened the tool once before.
      </p>
    </li>
    <li>
      <h3>A neural network estimates the ${term('depthmap', 'depth')}</h3>
      <p>
        Depth Anything V2 (small) looks at the picture and returns a distance for every pixel. It runs
        on your own machine: on your graphics card through ${term('webgpu', 'WebGPU')} where that is
        available, and on the processor otherwise. The weights are downloaded once, from Hugging Face,
        and then kept by your browser.
      </p>
      <p class="fine">
        What it returns is ${term('relative', 'relative')} — which things are nearer than which, with no
        real-world scale. Nothing here is a measurement in metres and it must not be used as one.
      </p>
    </li>
    <li>
      <h3>The picture becomes a sheet with hills and valleys</h3>
      <p>
        A grid of 65,000 points is laid over the photograph and each one is pushed towards or away from
        you according to its depth. Move the camera sideways and near points slide further than far
        ones — which is ${term('parallax', 'parallax')}, the thing that makes the world look solid when
        you move your head.
      </p>
    </li>
    <li>
      <h3>The travel is limited so it does not fall apart</h3>
      <p>
        Move too far and you are asking to see what was hidden behind the foreground, which nothing in
        the file knows. unflatten measures the sharpest depth edges in <em>your</em> photograph and works
        out how far the camera can go before the ${term('disocclusion', 'invented pixels')} become
        visible — then holds it there. The
        <button class="linklike" data-open="holemap-demo" type="button">hole map</button> shows you
        exactly which pixels are guesses.
      </p>
    </li>
    <li>
      <h3>Thirty frames a second, straight into a video file</h3>
      <p>
        Each frame is drawn on your graphics card and handed to the browser's own hardware video
        encoder. The camera path is built to loop seamlessly — it ends exactly where it began, moving at
        exactly the same speed — so the clip plays round and round without a jump.
      </p>
    </li>
  </ol>

  <h3>What else comes out</h3>
  <p>
    The depth field is worth having on its own, so it is written as a
    ${term('sixteenbit', '16-bit')} greyscale PNG rather than the ordinary 8-bit one — 65,536 levels
    instead of 256, which is the difference between a smooth surface and visible terracing. The same
    field drives a lens blur with a ${term('focus', 'focal plane')} you can drag, and a
    ${term('anaglyph', 'red/cyan 3D')} still.
  </p>
`;

export const PRIVACY = /* html */ `
  <p class="lede">
    The photographs people most want to bring to life are of their families, and often of people who
    have died. There is no way to un-upload one of those. So this tool does not upload them.
  </p>

  <h3>Protected</h3>
  <ul class="tick">
    <li>
      <strong>Your photograph never leaves this tab.</strong> It is read from your disk with the file
      picker, decoded in memory, and drawn on your own graphics card. No copy is sent anywhere, at any
      point, for any reason.
    </li>
    <li>
      <strong>Everything it produces is made here too.</strong> The clip, the depth map, the lens blur
      and the 3D still are all rendered and encoded on this machine and handed straight to your
      downloads folder.
    </li>
    <li>
      <strong>Nothing is written to disk.</strong> No cookies, no local storage, no database, no
      private file system. Reload the page and every trace of your photograph is gone. The build refuses
      to ship if any of those APIs appears in the code.
    </li>
    <li>
      <strong>There is no account, no counter and no limit.</strong> Nothing to sign up for, nothing
      metered, nothing that expires at the end of the month.
    </li>
    <li>
      <strong>It works offline.</strong> Once the page and the model are cached, aeroplane mode changes
      nothing. One caveat that is Apple's rather than ours: on iPhone and iPad, Safari clears cached
      data for sites you have not opened in about a week, so the model may have to come down again.
      Adding this page to your Home Screen prevents that.
    </li>
  </ul>

  <h3>Not protected</h3>
  <ul class="cross">
    <li>
      <strong>Whoever you send the clip to.</strong> Once you post the video, the ordinary rules of the
      internet apply. This tool has no say in it.
    </li>
    <li>
      <strong>The depth is a guess, and so are some of the pixels.</strong> Parts of the moving clip
      were never in the photograph — see the hole map. Do not treat any of it as evidence of what a
      scene actually looked like.
    </li>
    <li>
      <strong>Your browser knows you were here.</strong> History, cache and the service worker are all
      normal browser behaviour, on your own machine, and anyone with that machine can see them.
    </li>
  </ul>

  <h3>Trust surface</h3>
  <ul class="plain">
    <li>
      <strong>This page and its code</strong>, served by GitHub Pages over TLS. GitHub can see that a
      browser at your address asked for the page — as any web host can — and nothing about what you then
      opened with it.
    </li>
    <li>
      <strong>huggingface.co</strong> and its content network <strong>*.hf.co</strong>, on the first
      run only, for the model — three files: two small configuration files and the weights
      (<code>${MODEL_ID}</code>, ${mb(TIERS.webgpu.bytes)} on a graphics card or
      ${mb(TIERS.wasm.bytes)} on the processor). It is a plain download of a fixed, publicly listed
      file, pinned to one revision, and the weights themselves are served from a regional host under
      <code>*.hf.co</code>. Hugging Face learns that someone fetched a well-known model; it is sent no
      part of your photograph — the download happens before you have even chosen one — and after that
      first run nothing contacts it again.
    </li>
    <li>
      <strong>The model runtime is served from this site</strong>, not from a third-party CDN. The
      library it is built on would otherwise fetch ${mb(RUNTIME_BYTES_OVER_THE_WIRE)} of WebAssembly
      from jsDelivr on every run; this build serves those files itself, and its own security policy
      forbids the request.
    </li>
    <li>
      <strong>static.cloudflareinsights.com</strong>, on every page load. A Cloudflare Web Analytics
      beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your
      files/data are never sent to it.
    </li>
    <li>
      <strong>feedback.benrichardson.dev</strong>, on every page load, for the script behind the
      Feedback link at the bottom of this page. Feedback you choose to send is sent to
      feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send;
      your files and data never are.
    </li>
  </ul>
  <p class="fine">
    Those five are the whole list. The page's Content-Security-Policy allows no other origin, and the
    build fails if the policy and this list ever stop matching.
  </p>
`;

export const LIMITS = /* html */ `
  <p class="lede">
    unflatten builds a believable scene from one photograph. It is not a measuring instrument and it
    cannot see round corners. Here is exactly where the line is.
  </p>

  <h3>It invents pixels, and it says where</h3>
  <p>
    A single photograph records one view. Move the camera and you immediately ask to see what was behind
    the foreground — the wall behind someone's shoulder, the grass behind a fence post. That information
    does not exist anywhere in the file, so the nearest pixels are stretched across the gap. That is a
    guess, every implementation of this effect makes it, and the ones that look convincing are the ones
    that keep the guess small. unflatten measures how sharp your photograph's depth edges are and caps
    the camera's travel accordingly, then lets you look at the result with the guessed pixels painted
    red.
  </p>

  <h3>The depth has no scale</h3>
  <p>
    The network reports relative distance. It is genuinely good at ordering — this is nearer than that —
    and it knows nothing about metres. Two photographs of the same room can come back with different
    numeric ranges, which is why the amount of movement is worked out per picture rather than fixed.
  </p>

  <h3>The other artefact, which no hole map catches</h3>
  <p>
    The network's depth is soft at the edges of objects — softer than the real edge in the photograph.
    So the sheet starts bending slightly <em>before</em> the outline, and drags a thin band of
    background along with the subject. That reads as a faint halo, and it is a different fault from the
    stretching above: nothing is over-stretched, so the hole map stays quiet. Look for it around hair
    and shoulders against a distant background, and pull the travel back if it bothers you.
  </p>

  <h3>Where it struggles</h3>
  <ul class="plain">
    <li><strong>Glass, mirrors and water.</strong> A reflection is read as a surface at the depth of the reflected thing.</li>
    <li><strong>Fine detail against a distant background</strong> — hair, chain-link, bare branches. The depth edge is softer than the real one and the travel limit will come out small.</li>
    <li><strong>Flat scans of flat things.</strong> A photograph of a document has no depth to find, and the tool will say so rather than invent one.</li>
    <li><strong>Very wide panoramas.</strong> They work, but the network sees them at reduced detail.</li>
  </ul>

  <h3>What it is not</h3>
  <p>
    It cannot tell you how far away anything is, cannot reconstruct a scene for measurement or evidence,
    and cannot tell you whether a photograph is real. It makes a photograph move. That is the job.
  </p>
`;

export const ABOUT = /* html */ `
  <p class="lede">
    <strong>unflatten</strong> gives a flat photograph depth: a moving clip, a proper depth map, a lens
    blur applied years after the fact, and a red/cyan 3D still — all of it made on your own machine.
  </p>
  <p>
    It exists because the good versions of this effect are metered. The paid services upload your
    photograph and then charge by the conversion or stamp a watermark on it, and the free browser ones
    mostly fake the depth from brightness, which falls apart on any picture with a bright sky or a dark
    suit. There are honest free client-side depth-map generators — unflatten is not the first — but a
    depth map is not the thing most people came for. The clip is, and this one is unmetered, unbranded
    and made without your photograph going anywhere.
  </p>
  <p>
    Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>.
    It is one of a catalogue of tools and sites at
    <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a> —
    all of them free, all of them client-side.
  </p>
  <p>
    The depth model is
    <a href="https://huggingface.co/${MODEL_ID}" target="_blank" rel="noopener">${MODEL_ID}</a>,
    an ONNX conversion of Depth Anything V2 (small), Apache-2.0 licensed.
  </p>
  <p>
    Source, and the reasoning behind every decision in it, at
    <a href="https://github.com/ben-gy/unflatten" target="_blank" rel="noopener">github.com/ben-gy/unflatten</a>.
    Found a photograph it gets wrong? The feedback link at the bottom of the page reaches me — please
    describe the picture rather than sending it.
  </p>
`;
